import { StyleSheet } from 'react-native';

/**
 * Estilos das telas de configuração de perfil (onboarding pós-cadastro).
 * Auto-contido: não importa cores de outras telas, para o módulo poder ser
 * movido/removido sem arrastar dependência.
 */
export const getColors = (isDarkMode) => ({
  bg: isDarkMode ? '#0B0D17' : '#FFFFFF',
  surface: isDarkMode ? '#151821' : '#F4F6FA',
  surfaceAlt: isDarkMode ? '#1E2029' : '#EAEEF5',
  border: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
  textPrimary: isDarkMode ? '#E8EAED' : '#1A1A2E',
  textSecondary: isDarkMode ? '#A0A3B1' : '#5A5F73',
  textMuted: isDarkMode ? '#6B6E7B' : '#8B90A0',
  accent: '#03386C',
  accentSoft: isDarkMode ? 'rgba(91,143,249,0.18)' : 'rgba(3,56,108,0.10)',
  accentText: isDarkMode ? '#8FB4F9' : '#03386C',
  onAccent: '#FFFFFF',
});

export const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { paddingHorizontal: 20, paddingBottom: 24 },

  // ── Cabeçalho ──
  // `paddingTop` vem dos safe-area insets em runtime: sem isso o conteúdo sobe
  // por baixo da barra de status e colide com o relógio.
  header: { paddingHorizontal: 20, paddingBottom: 12 },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 20,
  },
  stepDot: { width: 8, height: 8, borderRadius: 4 },
  stepDotActive: { width: 26, height: 8, borderRadius: 4 },
  stepLabel: { fontSize: 12, fontWeight: '600', marginBottom: 8, letterSpacing: 0.5 },
  title: { fontSize: 24, fontWeight: '700', marginBottom: 6 },
  subtitle: { fontSize: 14, lineHeight: 20 },
  // Variante com mais peso, para quando o subtítulo carrega a AÇÃO da tela (e
  // não só um complemento). Chave separada de propósito: o passo 1 usa o
  // `subtitle` normal e não deve mudar.
  subtitleStrong: { fontSize: 17, fontWeight: '600', lineHeight: 24 },

  // ── Seções ──
  section: { marginTop: 26 },
  sectionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 4 },
  sectionHint: { fontSize: 12, marginBottom: 12 },

  // ── Busca de curso ──
  searchInput: {
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 14,
    fontSize: 15,
  },
  resultList: { marginTop: 8, borderRadius: 12, overflow: 'hidden', borderWidth: 1 },
  resultItem: { paddingHorizontal: 14, paddingVertical: 13, borderBottomWidth: 1 },
  resultLabel: { fontSize: 14, fontWeight: '500' },
  resultArea: { fontSize: 11, marginTop: 2 },
  emptyResult: { paddingHorizontal: 14, paddingVertical: 16, fontSize: 13 },

  // ── Curso escolhido ──
  selectedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 12,
  },
  selectedLabel: { fontSize: 15, fontWeight: '700', flexShrink: 1 },
  selectedArea: { fontSize: 12, marginTop: 2 },
  changeBtn: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16 },
  changeBtnText: { fontSize: 12, fontWeight: '700' },

  // ── Chips (áreas, ano, semestre) ──
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 20,
    borderWidth: 1,
  },
  chipText: { fontSize: 13, fontWeight: '600' },

  // Grade de semestres: 5 por linha, exatamente. A largura é calculada em JS a
  // partir da largura da tela — porcentagem não serve porque o `gap` entra na
  // conta e faria a 5ª coluna quebrar para a linha seguinte em telas estreitas.
  // O rótulo vai em duas linhas ("1º" / "semestre") porque a palavra inteira em
  // linha única não cabe em 1/5 da tela.
  semestreChip: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 9,
    borderRadius: 12,
    borderWidth: 1,
  },
  semestreNum: { fontSize: 15, fontWeight: '700' },
  semestreWord: { fontSize: 10, fontWeight: '600', marginTop: 1 },
  chipLock: { fontSize: 11 },

  // ── Rodapé ──
  // `paddingBottom` também recebe o inset em runtime (barra de gestos).
  footer: { paddingHorizontal: 20, paddingTop: 12, borderTopWidth: 1 },
  footerHint: { fontSize: 12, textAlign: 'center', marginBottom: 10 },
  primaryBtn: { height: 52, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  primaryBtnText: { fontSize: 16, fontWeight: '700' },
  ghostBtn: { height: 44, alignItems: 'center', justifyContent: 'center', marginTop: 6 },
  ghostBtnText: { fontSize: 14, fontWeight: '600' },

  // ── Foto ──
  photoWrap: { alignItems: 'center', marginTop: 40 },
  avatar: {
    width: 160,
    height: 160,
    borderRadius: 80,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    overflow: 'hidden',
  },
  avatarImage: { width: '100%', height: '100%' },
  // O selo do lápis fica FORA do círculo recortado (o avatar tem overflow
  // hidden), por isso o toque envolve os dois num wrapper — assim qualquer
  // ponto, inclusive o selo, abre a galeria.
  avatarTouch: { width: 160, height: 160, alignItems: 'center', justifyContent: 'center' },
  avatarBadge: {
    position: 'absolute',
    right: 2,
    bottom: 2,
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
  },
  avatarIcon: { fontSize: 48 },
  photoHint: { fontSize: 13, textAlign: 'center', marginTop: 16, paddingHorizontal: 32, lineHeight: 19 },
});
